import { Car, Ship, MapPin, Shield, Hotel, ArrowRight, Plane, Users, Building, Star, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useFranchiseData } from '../hooks/useFranchiseData'

const services = [
  {
    name: 'Island Holidays',
    description: 'Escape to paradise with our exclusive island holiday packages. From tropical getaways to luxury island resorts, we offer unforgettable island experiences.',
    icon: Car,
    features: [
      'Tropical island destinations',
      'Luxury island resorts',
      'All-inclusive packages',
      'Water sports activities',
      'Island hopping tours'
    ]
  },
  {
    name: 'Cruises',
    description: 'Discover the world\'s most beautiful destinations by sea. From luxury ocean liners to intimate river cruises, we offer unforgettable maritime adventures.',
    icon: Ship,
    features: [
      'Ocean and river cruises',
      'Luxury cruise lines',
      'All-inclusive packages',
      'Shore excursions',
      'Family-friendly options'
    ]
  },
  {
    name: 'Tours',
    description: 'Guided tours and experiences that bring destinations to life. From cultural immersions to adventure expeditions, our tours are designed for every type of traveler.',
    icon: MapPin,
    features: [
      'Guided cultural tours',
      'Adventure expeditions',
      'Custom itineraries',
      'Expert local guides',
      'Small group experiences'
    ]
  },
  {
    name: 'Travel Insurance',
    description: 'Comprehensive travel protection for peace of mind. Our insurance plans cover medical emergencies, trip cancellations, lost luggage, and more.',
    icon: Shield,
    features: [
      'Medical coverage',
      'Trip cancellation protection',
      'Lost luggage coverage',
      'Emergency assistance',
      'Flexible plan options'
    ]
  },
  {
    name: 'Sports Travel',
    description: 'Experience the thrill of sports travel with our specialized packages. From golf getaways to adventure sports, we cater to sports enthusiasts worldwide.',
    icon: Hotel,
    features: [
      'Golf destination packages',
      'Adventure sports tours',
      'Sports event tickets',
      'Professional training camps',
      'Sports equipment rental'
    ]
  }
]

const mainServices = [
  {
    title: "Leisure Travel",
    description: "Personalized travel experiences crafted just for you",
    icon: Plane,
    features: [
      "Custom itinerary planning",
      "Flight & accommodation booking",
      "Travel insurance & visa assistance",
      "24/7 support during your trip"
    ],
    buttonStyle: "white"
  },
  {
    title: "Family Travel",
    description: "Creating magical memories for the whole family",
    icon: Users,
    features: [
      "Child-friendly destinations",
      "Family package deals",
      "Activities for all ages",
      "Safe & secure travel arrangements"
    ],
    buttonStyle: "gradient"
  },
  {
    title: "Business Travel",
    description: "Professional travel management for corporate clients",
    icon: Building,
    features: [
      "Corporate travel policies",
      "Expense management",
      "Group booking discounts",
      "Priority customer service"
    ],
    buttonStyle: "white"
  }
]

const additionalServices = [
  {
    title: "Flights",
    description: "International and domestic flight bookings",
    icon: Plane
  },
  {
    title: "Hotels & Resorts",
    description: "Luxury hotels, boutique resorts & exclusive vacation rentals",
    icon: Hotel
  },
  {
    title: "Ski Holidays",
    description: "Winter sports and mountain adventures",
    icon: Star
  },
  {
    title: "Rail Travel",
    description: "Scenic train journeys and rail passes",
    icon: MapPin
  },
  {
    title: "Self-Drive Getaways",
    description: "Freedom to explore at your own pace",
    icon: Car
  },
  {
    title: "Group Travel",
    description: "Organized tours for families and groups",
    icon: Users
  },
  {
    title: "Visa Assistance",
    description: "Documentation and visa application support",
    icon: Shield
  },
  {
    title: "Island Holidays",
    description: "Paradise island getaways and tropical escapes",
    icon: Car
  },
  {
    title: "Travel Insurance",
    description: "Comprehensive coverage for peace of mind",
    icon: Shield
  },
  {
    title: "Cruises",
    description: "Ocean and river cruise experiences",
    icon: Ship
  },
  {
    title: "Tours & Activities",
    description: "Guided tours and exciting activities",
    icon: Star
  },
  {
    title: "Sports Travel",
    description: "Golf, adventure sports and athletic experiences",
    icon: Clock
  }
]

export default function Services() {
  const { franchiseData, loading } = useFranchiseData()

  if (loading || !franchiseData) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-48 mx-auto"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white">
      {/* Header Section */}
      <section className="relative py-24 bg-gradient-to-r from-purple-600/80 to-purple-600/80" style={{
        backgroundImage: `linear-gradient(rgba(147,51,234,0.8), rgba(147,51,234,0.8)), url('/stars1.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Our Story
          </h1>
          <p className="text-xl text-white max-w-3xl mx-auto">
            From humble beginnings in 1994 to becoming one of Cape Town's leading leisure travel specialists, Marina's journey is built on passion, experience, and dedication to creating unforgettable memories.
          </p>
        </div>
      </section>

      {/* Story Cards Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-10">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-purple-600 mb-4">Early Career</h3>
              <p className="text-gray-700">
                Marina's eagerness to be involved in Travel & Tourism began in 1994 while exploring the UK and Europe. She studied Travel & Tourism via correspondence while working with a renowned European coach touring company in London.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-purple-600 mb-4">Building Experience</h3>
              <p className="text-gray-700">
                Returning to South Africa in 1997, Marina expanded her skills in Cape Town and Port Elizabeth, gaining valuable industry experience and shaping her vision for the future.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-purple-600 mb-4">Stars in Transition</h3>
              <p className="text-gray-700">
                In 2008, she founded Stars in Transition, specializing in family and leisure travel. The business flourished through repeat clients and referrals, supporting individuals, families, and groups worldwide.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-16">
            <div className="text-2xl font-medium italic text-purple-600 max-w-4xl mx-auto">
              "Making memorable and fun-filled safe travel and holidays."
            </div>
          </div>
        </div>
      </section>

      {/* Marina Bio Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-12 md:p-16">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              {/* Marina's Picture */}
              <div className="flex-shrink-0">
                <img 
                  src="/marina.jpg" 
                  alt="Marina Doms - Personal Travel Consultant" 
                  className="w-72 h-72 rounded-full object-cover shadow-xl"
                />
              </div>

              {/* Marina's Bio */}
              <div className="flex-1">
                <h2 className="text-3xl md:text-4xl font-bold text-purple-600 mb-6">
                  Meet Marina Doms
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Marina, your Personal Travel Consultant, brings together a passion for travel and extensive expertise since 1994. From romantic getaways to multi-destination adventures, she ensures every detail is thoughtfully crafted and personalized.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Her tailor-made itineraries and wide network of global partners ensure unforgettable travel experiences for individuals, families, and groups.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services Section */}
      <section className="py-24 bg-gradient-to-r from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              Comprehensive Travel Services
            </h2>
            <p className="text-lg text-gray-700 max-w-7xl mx-auto">
            Flights, accommodation, transfers, car hire, guided tours, self-drive getaways, cruises, rail travel, ski-packages, island getaways, group travel, visa assistance, travel insurance … and more.
            </p>
            <p className="text-lg text-gray-700 max-w-7xl mx-auto">
            In essence you are offered bespoke travel options, flexibility, and personal service, ensuring your requirements are met efficiently and professionally, making for memorable and fun-filled safe travel and holidays.
            </p>
          </div>

          {/* Additional Service Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
            {additionalServices.map((service, index) => {
              // Check if this is the Travel Insurance service
              const isTravelInsurance = service.title === "Travel Insurance"
              
              // Define which services should be linked to pages
              const serviceLinks: { [key: string]: string } = {
                "Flights": "/services/flights",
                "Ski Holidays": "/services/ski-holidays",
                "Rail Travel": "/services/rail-travel",
                "Island Holidays": "/services/island-holidays",
                "Cruises": "/services/cruises",
                "Tours & Activities": "/services/tours",
                "Sports Travel": "/services/sports-travel"
              }
              
              const cardContent = (
                <>
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-white" />
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">{service.title}</h3>
                  <p className="text-gray-600 text-sm text-center">{service.description}</p>
                </>
              )
              
              // If it's Travel Insurance, link to external site
              if (isTravelInsurance) {
                return (
                  <a
                    key={index}
                    href="https://travelinsurance.brytesa.com/direct?organisationcode=starsintransition&nisorganisationcode=SAP-1109537"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl hover:scale-105 hover:-translate-y-2 transition-all duration-300 group block"
                  >
                    {cardContent}
                  </a>
                )
              }
              
              // If it has a page link, make it clickable
              if (serviceLinks[service.title]) {
                return (
                  <Link
                    key={index}
                    to={serviceLinks[service.title]}
                    className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl hover:scale-105 hover:-translate-y-2 transition-all duration-300 group block"
                  >
                    {cardContent}
                  </Link>
                )
              }
              
              // Default: regular card
              return (
                <div 
                  key={index} 
                  className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl hover:scale-105 hover:-translate-y-2 transition-all duration-300 group"
                >
                  {cardContent}
                </div>
              )
            })}
          </div>

          {/* Travel Insurance Link */}
          <div className="mt-12 text-center">
            <a
              href="https://travelinsurance.brytesa.com/direct?organisationcode=starsintransition&nisorganisationcode=SAP-1109537"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <Shield className="h-5 w-5" />
              Get Travel Insurance Quote
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <div className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Ready to Start Your Journey?
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Let us help you plan the perfect trip with our comprehensive travel services.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/contact"
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold px-8 py-3 rounded-lg hover:opacity-90 transition"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
